import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  deadlinesApi,
  type ComplianceProfile,
  type DeadlineComputeResponse,
  type DeadlineHorizon,
} from '../lib/api';

const ENTITY_OPTIONS = [
  '501c3', '501c4', '501c5', '501c6', '501c7', '501c8', '501c9', '501c10', '501c19',
];

const STATE_ABBRS = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

function defaultHorizon(): DeadlineHorizon {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear() + 1, now.getUTCMonth(), 1));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

export default function Deadlines() {
  const [profile, setProfile] = useState<ComplianceProfile>({
    entityKey: '501c3',
    jurisdiction: '',
    formationDate: '',
    fiscalYearEnd: { month: 12, day: 31 },
    annualRevenue: undefined,
    totalAssets: undefined,
    hasEmployees: false,
  });
  const [horizon, setHorizon] = useState<DeadlineHorizon>(defaultHorizon());
  const [skipFederal, setSkipFederal] = useState(false);
  const [skipCharitable, setSkipCharitable] = useState(false);
  const [skipAnnualReports, setSkipAnnualReports] = useState(false);
  const [excludeQualitative, setExcludeQualitative] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DeadlineComputeResponse | null>(null);
  const [error, setError] = useState('');

  const run = async () => {
    setLoading(true);
    setError('');
    try {
      // Empty date and select fields must be dropped rather than sent as '',
      // which the API would reject or misread as an answer.
      const cleanProfile: ComplianceProfile = {
        ...profile,
        jurisdiction: profile.jurisdiction || undefined,
        formationDate: profile.formationDate || undefined,
        incorporationDate: profile.incorporationDate || undefined,
        irsSubmissionDate: profile.irsSubmissionDate || undefined,
        irsDeterminationLetterDate: profile.irsDeterminationLetterDate || undefined,
        foundationStatus: profile.foundationStatus || undefined,
        filingType: profile.filingType || undefined,
      };
      const res = await deadlinesApi.compute(cleanProfile, horizon, {
        skipFederal,
        skipCharitable,
        skipAnnualReports,
        excludeQualitative,
      });
      setResult(res);
    } catch (e) {
      setError((e as Error).message || 'Failed to compute deadlines');
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => {
    if (!result) return [];
    const out: Record<string, typeof result.occurrences> = {};
    for (const o of result.occurrences) {
      const key = o.dueDate ? o.dueDate.slice(0, 7) : 'qualitative';
      out[key] ??= [];
      out[key].push(o);
    }
    return Object.entries(out).sort(([a], [b]) => a.localeCompare(b));
  }, [result]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>
        Deadlines Engine
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Run the structured deadline rules engine for a sample organization profile. Loads
        every applicable <code>DeadlineRule</code> from the reference data, evaluates the
        attached conditions, and projects them across the horizon.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Compliance Profile
            </Typography>
            <Stack spacing={2}>
              <TextField
                select
                label="Entity type"
                size="small"
                value={profile.entityKey ?? ''}
                onChange={(e) => setProfile({ ...profile, entityKey: e.target.value })}
              >
                {ENTITY_OPTIONS.map((k) => (
                  <MenuItem key={k} value={k}>{k}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Jurisdiction (state abbr.)"
                size="small"
                value={profile.jurisdiction ?? ''}
                onChange={(e) => setProfile({ ...profile, jurisdiction: e.target.value || undefined })}
                helperText="Leave blank to evaluate every state's rules"
              >
                <MenuItem value="">All states</MenuItem>
                {STATE_ABBRS.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </TextField>
              <TextField
                type="date"
                label="Formation date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={profile.formationDate ?? ''}
                onChange={(e) => setProfile({ ...profile, formationDate: e.target.value })}
              />

              <TextField
                select
                label="Filing type"
                size="small"
                value={profile.filingType ?? ''}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    filingType: (e.target.value || undefined) as ComplianceProfile['filingType'],
                  })
                }
                helperText="Initial filings often follow formation rather than the fiscal year"
              >
                <MenuItem value="">Not specified (treated as renewal)</MenuItem>
                <MenuItem value="initial">Initial — first-ever filing</MenuItem>
                <MenuItem value="renewal">Renewal</MenuItem>
              </TextField>

              <TextField
                select
                label="Foundation status"
                size="small"
                value={profile.foundationStatus ?? ''}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    foundationStatus: (e.target.value ||
                      undefined) as ComplianceProfile['foundationStatus'],
                  })
                }
                helperText="Leave unspecified and both the 990 and 990-PF are reported"
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="publicCharity">Public charity</MenuItem>
                <MenuItem value="privateFoundation">Private foundation</MenuItem>
              </TextField>

              {/* Formation-event anchors. Only shown for initial filings, which
                  are the only rules that use them. */}
              {profile.filingType === 'initial' && (
                <Stack spacing={2}>
                  {(
                    [
                      ['incorporationDate', 'Incorporation date'],
                      ['irsSubmissionDate', 'IRS submission date'],
                      ['irsDeterminationLetterDate', 'IRS determination letter date'],
                    ] as const
                  ).map(([key, label]) => (
                    <TextField
                      key={key}
                      type="date"
                      label={label}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={profile[key] ?? ''}
                      onChange={(e) => setProfile({ ...profile, [key]: e.target.value })}
                    />
                  ))}
                </Stack>
              )}

              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Filing status per agency
                </Typography>
              </Divider>
              {(
                [
                  ['charitableSolicitation', 'Charitable solicitation'],
                  ['annualReport', 'Annual report'],
                  ['federal', 'Federal filings'],
                ] as const
              ).map(([key, label]) => (
                <TextField
                  key={key}
                  select
                  label={label}
                  size="small"
                  value={profile.filingStatus?.[key] ?? ''}
                  onChange={(e) => {
                    const next = { ...(profile.filingStatus ?? {}) };
                    if (e.target.value) next[key] = e.target.value as 'current' | 'pastDue' | 'revoked';
                    else delete next[key];
                    setProfile({
                      ...profile,
                      filingStatus: Object.keys(next).length > 0 ? next : undefined,
                    });
                  }}
                >
                  <MenuItem value="">Not specified (treated as current)</MenuItem>
                  <MenuItem value="current">Current</MenuItem>
                  <MenuItem value="pastDue">Past due</MenuItem>
                  <MenuItem value="revoked">Revoked</MenuItem>
                </TextField>
              ))}
              <Divider />
              <Stack direction="row" spacing={2}>
                <TextField
                  type="number"
                  label="FYE month"
                  size="small"
                  value={profile.fiscalYearEnd?.month ?? 12}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      fiscalYearEnd: {
                        month: Number(e.target.value) || 12,
                        day: profile.fiscalYearEnd?.day ?? 31,
                      },
                    })
                  }
                  inputProps={{ min: 1, max: 12 }}
                  sx={{ width: 110 }}
                />
                <TextField
                  type="number"
                  label="FYE day"
                  size="small"
                  value={profile.fiscalYearEnd?.day ?? 31}
                  onChange={(e) =>
                    setProfile({
                      ...profile,
                      fiscalYearEnd: {
                        month: profile.fiscalYearEnd?.month ?? 12,
                        day: Number(e.target.value) || 31,
                      },
                    })
                  }
                  inputProps={{ min: 1, max: 31 }}
                  sx={{ width: 110 }}
                />
              </Stack>
              <TextField
                type="number"
                label="Annual revenue (USD)"
                size="small"
                value={profile.annualRevenue ?? ''}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    annualRevenue: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
              />
              <TextField
                type="number"
                label="Total assets (USD)"
                size="small"
                value={profile.totalAssets ?? ''}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    totalAssets: e.target.value === '' ? undefined : Number(e.target.value),
                  })
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!profile.hasEmployees}
                    onChange={(_, v) => setProfile({ ...profile, hasEmployees: v })}
                  />
                }
                label="Has employees"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!profile.isChurch}
                    onChange={(_, v) => setProfile({ ...profile, isChurch: v })}
                  />
                }
                label="Is a church"
              />
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Horizon &amp; options
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={horizon.from}
                  onChange={(e) => setHorizon({ ...horizon, from: e.target.value })}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={horizon.to}
                  onChange={(e) => setHorizon({ ...horizon, to: e.target.value })}
                />
              </Stack>
              <Divider />
              <FormControlLabel
                control={<Switch checked={skipFederal} onChange={(_, v) => setSkipFederal(v)} />}
                label="Skip federal filings"
              />
              <FormControlLabel
                control={<Switch checked={skipCharitable} onChange={(_, v) => setSkipCharitable(v)} />}
                label="Skip charitable solicitation"
              />
              <FormControlLabel
                control={
                  <Switch checked={skipAnnualReports} onChange={(_, v) => setSkipAnnualReports(v)} />
                }
                label="Skip state annual reports"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={excludeQualitative}
                    onChange={(_, v) => setExcludeQualitative(v)}
                  />
                }
                label="Exclude qualitative (event-based)"
              />
              <Box>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={run}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? 'Computing…' : 'Run engine'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {result && (
        <Box mt={4}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Typography variant="h6" fontWeight={700}>
              Results
            </Typography>
            <Chip
              size="small"
              label={`${result.totals.occurrences} occurrence${
                result.totals.occurrences === 1 ? '' : 's'
              }`}
              color="primary"
            />
            <Chip
              size="small"
              label={`${result.totals.rulesEvaluated} rules evaluated`}
              variant="outlined"
            />
          </Stack>

          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {result.occurrences.length === 0 && (
            <Alert severity="info">
              No deadlines fall in this horizon. Try widening the range or filling in more
              of the profile (e.g. formation date is required for most state annual reports).
            </Alert>
          )}

          {grouped.map(([bucket, occs]) => (
            <Box key={bucket} mb={3}>
              <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">
                {bucket === 'qualitative' ? 'Qualitative (event-based)' : monthLabel(bucket)}
              </Typography>
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={120}>Window opens</TableCell>
                        <TableCell width={120}>Due</TableCell>
                        <TableCell width={70}>State</TableCell>
                        <TableCell>Label</TableCell>
                        <TableCell>Recurrence</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Extension</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {occs.map((o, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            {o.windowOpensDate ? (
                              <Tooltip
                                title={
                                  o.processingTime
                                    ? `Processing: ${o.processingTime.minDays ?? '?'}–${
                                        o.processingTime.maxDays ?? '?'
                                      } ${o.processingTime.businessDays !== false ? 'business ' : ''}days`
                                    : 'Filing window opens'
                                }
                              >
                                <span>{o.windowOpensDate}</span>
                              </Tooltip>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {o.dueDate ?? (
                              <Chip size="small" label="qualitative" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell>{o.jurisdiction ?? '—'}</TableCell>
                          <TableCell>
                            <strong>{o.label}</strong>
                            {o.qualitativeText && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {o.qualitativeText}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{o.recurrence?.kind ?? 'annual'}</TableCell>
                          <TableCell>
                            <code style={{ fontSize: 11 }}>{o.source?.collection}</code>
                          </TableCell>
                          <TableCell>
                            {o.extendedDueDate ? (
                              <Tooltip
                                title={
                                  // Each window runs from the end of the last,
                                  // so show the whole chain, not just the end.
                                  (o.extendedDueDates ?? [o.extendedDueDate])
                                    .map((d, i) => {
                                      const ext = o.extensions?.[i] ?? o.extension;
                                      const via = ext?.form ? ` via ${ext.form}` : '';
                                      const auto = ext?.automatic ? ' (automatic)' : '';
                                      return `${i + 1}. ${d}${via}${auto}`;
                                    })
                                    .join(' · ')
                                }
                              >
                                <span>
                                  {o.extendedDueDate}
                                  {(o.extendedDueDates?.length ?? 0) > 1 && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={`${o.extendedDueDates!.length} windows`}
                                      sx={{ ml: 0.5 }}
                                    />
                                  )}
                                </span>
                              </Tooltip>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function Tooltip({ title, children }: { title: string; children: React.ReactNode }) {
  // Lightweight stand-in to avoid pulling in MUI Tooltip just for a hover hint.
  return (
    <span title={title} style={{ borderBottom: '1px dotted #999', cursor: 'help' }}>
      {children}
    </span>
  );
}

function monthLabel(yyyymm: string): string {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}
